import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useHookstate } from "@hookstate/core";
import { SITEID_GLOBAL_STATE } from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import {
    Header,
    GridContainer,
    LeftSpace
} from "pages/People/PeopleStyled";
import Settings from "assets/svgIcons/PeopleSubSideNav/Settings";
import PeopleIcon from "assets/svgIcons/SideNav/PeopleIcon";
import { process } from '@progress/kendo-data-query';
import { featureKey } from "constant";
import EyeIcon from "assets/svgIcons/PeopleSubSideNav/EyeIcon";
import EyeSlashIcon from "assets/svgIcons/PeopleSubSideNav/EyeSlashIcon";
import AddPeopleModal from "./addPeopleModal";
import AddGroupsModal from "./addGroupsModal";

const PAGE_SIZE_KEY = 'vehicleGridPageSize';

const PAGE_SIZE_OPTIONS = [15, 20, 25, 30, 40, 50, 100, 150, 200, 250];

const VehiclePage = React.memo(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getVehiclesData, assignPeopleData, assignGroupData } = useSelector((state) => state.peopleReducers) || {};
    const { siteSettingsData } = useSelector((state) => state.commonReducers) || {};

    const getInitialPageSize = () => {
        const saved = localStorage.getItem(PAGE_SIZE_KEY);
        return saved === 'All' ? vehicles.length : parseInt(saved) || 25;
    };

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const [vehicles, setVehicles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataState, setDataState] = useState({ skip: 0, take: getInitialPageSize(), sort: [], filter: null });
    const [addPeopleModalOpen, setAddPeopleModalOpen] = useState(false);
    const [gpsiCarId, setGpsiCarId] = useState(0);
    const [addGroupsModalOpen, setAddGroupsModalOpen] = useState(false);

    useEffect(() => {
        if (Object.keys(siteSettingsData).length > 0) {
            if (!siteSettingsData?.Features?.includes(featureKey.resortEdition)) {
                navigate("/dashboard", { replace: true });
            }

            dispatch(PEOPLE_SERVICES.getPeopleBySiteIdService(siteId.get()));
            dispatch(PEOPLE_SERVICES.getGroupsBySiteIdService(siteId.get()));
            dispatch(PEOPLE_SERVICES.getVehiclesBySiteIdService(siteId.get()));
        }
    }, [siteId.get(), assignPeopleData, assignGroupData, siteSettingsData, dispatch, navigate]);

    useEffect(() => {
        if (getVehiclesData) {
            setVehicles(getVehiclesData);
            setIsLoading(false);
        }
    }, [getVehiclesData]);

    const actionsCell = (props) => {
        return (
            <td>
                <button
                    title={t("Add_People")}
                    className="k-button"
                    style={{ marginRight: 10 }}
                    onClick={() => { setGpsiCarId(props.dataItem.GpsiCarId); setAddPeopleModalOpen(true);}}
                >
                    <PeopleIcon color={"#000"} width={15} height={15} />
                </button>
                <button
                    title={t("Add_Groups")}
                    className="k-button"
                    onClick={() => { setGpsiCarId(props.dataItem.GpsiCarId); setAddGroupsModalOpen(true); }}
                >
                    <Settings color={"#000"} width={15} height={15} />
                </button>
            </td>
        );
    };

    const processedData = process(vehicles ?? [], dataState);

    const handleDataStateChange = (event) => {
        let { take, skip } = event.dataState;

        if (take === 'All') {
            take = vehicles.length;
            skip = 0;
        }

        setDataState({
        ...event.dataState,
            take,
            skip,
        });
    };

    const HtmlCell = (props) => {
        const html = props.dataItem[props.field];

        return (
            <td dangerouslySetInnerHTML={{ __html: html }} />
        );
    };

    return (
        <>
            <Header>
                <LeftSpace>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 3, marginBottom: 15, color: "#fff" }}>
                        <span title={t("Visible_Vehicles_Count")} >
                            <EyeIcon style={{ verticalAlign: 'middle' }} color="#fff" />
                            {vehicles.length}
                        </span>
                        <span title={t("Invisible_Vehicles_Count")}>
                            <EyeSlashIcon style={{ verticalAlign: 'middle' }} color="#fff" />
                            {processedData.total - vehicles.length}
                        </span>
                    </div>
                </LeftSpace>
            </Header>

            <GridContainer>
                <Grid
                    data={processedData.data}
                    pageable={{
                                buttonCount: 5,
                                pageSizes: ['All', ...PAGE_SIZE_OPTIONS],
                                pageSizeValue: dataState.take === vehicles.length ? 'All' : dataState.take,
                            }}
                    sortable
                    filterable
                    loading={isLoading}
                    {...dataState}
                    onDataStateChange={handleDataStateChange}
                    total={processedData.total}
                >
                    <Column title="" cell={actionsCell} filterable={false} width={100} />
                    <Column field="VehicleNumber" title={t("Vehicle_Number")} />
                    <Column field="VehicleMake" title={t("Vehicle_Make")} />
                    <Column field="VehicleModel" title={t("Vehicle_Model")} />
                    <Column field="People" title={t("People")} cell={HtmlCell} />
                    <Column field="Groups" title={t("Group(s)")} />
                    <Column field="TotalNumberOfAssignedUsers" title={t("Total_Number_Of_Assigned_Users")} width={230} />
                </Grid>
            </GridContainer>

            <AddPeopleModal 
                gpsiCarId={gpsiCarId} 
                addPeopleModalOpen={addPeopleModalOpen} 
                setAddPeopleModalOpen={setAddPeopleModalOpen} />

            <AddGroupsModal 
                gpsiCarId={gpsiCarId} 
                addGroupsModalOpen={addGroupsModalOpen} 
                setAddGroupsModalOpen={setAddGroupsModalOpen} />
        </>
    );
});

export default VehiclePage;